import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, X, Trophy, AlertTriangle, RotateCcw, Zap, Flag } from "lucide-react";
import api, { apiError } from "@/lib/api";
import RacingKeyboard from "@/components/RacingKeyboard";
import RacePositions from "@/components/RacePositions";
import { Speedometer } from "@/components/Speedometer";
import { tokenizeTarget, charsDone } from "@/lib/raceTokens";
import { iosHaptic } from "@/lib/haptics";
import { hapticsEnabled, soundEnabled, useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";

// Four tires = four mistakes. The 4th disqualifies the racer's TIME (DQT) - they
// still finish and earn credit for characters traversed, but the run can't win a prize.
const MAX_MISTAKES = 4;
// Mistakes light the tires clockwise from the top-right corner.
const TIRE_ORDER = ["tr", "br", "bl", "tl"];

function haptic(ms) {
  if (!hapticsEnabled()) return;
  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
    try { if (navigator.vibrate(ms)) return; } catch (e) { /* fall through */ }
  }
  iosHaptic();
}

function beep(freq = 660, dur = 0.09) {
  if (!soundEnabled()) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = freq;
    gain.gain.value = 0.05;
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + dur);
    osc.onended = () => ctx.close();
  } catch { /* no-op */ }
}

/** A corner tire. Black by default; turns red (with a white stroke + X) once a
 *  mistake lights it. A lit tire is the RESTART control - until the 4th mistake
 *  disqualifies the time, at which point the tires only display the penalty. */
function Tire({ pos, lit, clickable, onClick, showHint }) {
  const place = {
    tl: "-left-[6px] top-14",
    tr: "-right-[6px] top-14",
    bl: "-left-[6px] bottom-20",
    br: "-right-[6px] bottom-20",
  }[pos];
  return (
    <div className={`absolute ${place} z-20 flex items-center`}>
      {pos === "tr" && lit && showHint && (
        <span className="absolute right-7 -top-6 whitespace-nowrap text-[9px] font-bold uppercase tracking-wide bg-white text-neutral-900 border border-neutral-900 rounded px-1.5 py-0.5">
          Tap to restart
        </span>
      )}
      <button
        type="button"
        onClick={clickable ? onClick : undefined}
        disabled={!clickable}
        data-testid={`tire-${pos}`}
        aria-pressed={lit}
        className={`w-[13px] h-[54px] rounded-md transition-all duration-200 flex items-center justify-center
          ${lit ? "bg-race-red ring-2 ring-white" : "bg-neutral-900"} ${clickable ? "cursor-pointer" : "cursor-default"}`}
        style={lit ? { boxShadow: "0 0 14px rgba(255,42,0,0.85)" } : { boxShadow: "none" }}
      >
        {lit && <X size={9} className="text-white" strokeWidth={3} />}
      </button>
    </div>
  );
}

export default function RaceExperience({ race, sponsor, onExit, onAgain, preview = false }) {
  const { settings } = useSettings();
  const { user } = useAuth();
  const target = race.target || "";
  const tokens = useMemo(() => tokenizeTarget(target), [target]);
  const transmission = race.transmission || race.keyboard.length;
  const penalty = race.penalty_seconds;

  // Cursor-hold model: `pos` = next expected token index. A wrong tap holds the cursor.
  const initialPos = preview ? Math.ceil(tokens.length * 0.45) : 0;
  const [phase, setPhase] = useState(preview ? "racing" : "ready");
  const [count, setCount] = useState(3);
  const [pos, setPos] = useState(initialPos);
  const [streak, setStreak] = useState(preview ? charsDone(tokens, initialPos) : 0);
  const [elapsed, setElapsed] = useState(preview ? 3.24 : 0);
  const [flash, setFlash] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [dqt, setDqt] = useState(false);
  const [showRestart, setShowRestart] = useState(false);
  const [result, setResult] = useState(null);
  const [ghostProg, setGhostProg] = useState(race.ghosts.map((_, i) => (preview ? 0.2 + i * 0.15 : 0)));

  const startRef = useRef(0);
  const ksRef = useRef([]);
  const focusLostRef = useRef(false);
  const rafRef = useRef(null);
  const posRef = useRef(initialPos);
  const penalizedRef = useRef(new Set());

  useEffect(() => {
    const onBlur = () => { if (phase === "racing") focusLostRef.current = true; };
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [phase]);

  const loop = useCallback(() => {
    const sec = (performance.now() - startRef.current) / 1000;
    setElapsed(sec);
    setGhostProg(race.ghosts.map((g) => Math.min(1, sec / (target.length / g.cps))));
    rafRef.current = requestAnimationFrame(loop);
  }, [race.ghosts, target.length]);

  const finish = useCallback(async () => {
    cancelAnimationFrame(rafRef.current);
    setPhase("submitting");
    try {
      const { data } = await api.post("/races/submit", {
        race_id: race.race_id,
        keystrokes: ksRef.current,
        focus_lost: focusLostRef.current,
      });
      setResult(data);
      setPhase("results");
      beep(880, 0.18);
      if (data.placement === 1 && !data.dqt) haptic([40, 60, 40]);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setPhase("racing");
    }
  }, [race.race_id]);

  const handlePress = useCallback((key) => {
    if (phase !== "racing") return;
    const value = key === "space" ? " " : key;
    const t = performance.now() - startRef.current;
    ksRef.current.push({ v: value, t });

    const cur = posRef.current;
    const expected = tokens[cur];
    if (expected != null && value.toLowerCase() === expected.toLowerCase()) {
      // correct → advance cursor, extend the accurate streak
      const np = cur + 1;
      posRef.current = np;
      setPos(np);
      setStreak((s) => s + expected.length);
      haptic(12);
      if (np >= tokens.length) finish();
      return true;
    } else {
      // miss → cursor HOLDS; light one tire (single penalty per position); reset glow
      setStreak(0);
      setFlash((f) => f + 1);
      if (!penalizedRef.current.has(cur)) {
        penalizedRef.current.add(cur);
        setMistakes((m) => {
          const nm = m + 1;
          if (nm >= MAX_MISTAKES) { setDqt(true); haptic([80, 40, 80, 40, 120]); }
          else haptic([50, 30, 50]);
          return nm;
        });
      } else haptic([50, 30, 50]);
      return false;
    }
  }, [phase, tokens, finish]);

  const startCountdown = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    startRef.current = 0;
    ksRef.current = [];
    posRef.current = 0;
    penalizedRef.current = new Set();
    setPos(0);
    setStreak(0);
    setMistakes(0);
    setDqt(false);
    setShowRestart(false);
    setElapsed(0);
    focusLostRef.current = false;
    setPhase("countdown");
    setCount(3);
    let c = 3;
    const iv = setInterval(() => {
      c -= 1;
      if (c <= 0) {
        clearInterval(iv);
        startRef.current = performance.now();
        setPhase("racing");
        beep(990, 0.16);
        rafRef.current = requestAnimationFrame(loop);
      } else { setCount(c); beep(560, 0.07); }
    }, 800);
  }, [loop]);

  // RESTART (pit stop): wipe the track + mistakes for a clean run but DO NOT touch the
  // race clock. The racer trades elapsed time for a shot at 100% accuracy.
  const handleRestart = () => {
    setShowRestart(false);
    ksRef.current = [];
    posRef.current = 0;
    penalizedRef.current = new Set();
    setPos(0);
    setStreak(0);
    setMistakes(0);
    setDqt(false);
    haptic(20);
  };

  const openRestart = () => { if (!dqt) setShowRestart(true); };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  // Immersive race mode: hide global header/footer while the vehicle screen is up.
  useEffect(() => {
    const on = ["ready", "countdown", "racing", "submitting"].includes(phase);
    document.body.classList.toggle("immersive-race", on);
    return () => document.body.classList.remove("immersive-race");
  }, [phase]);

  const guide = race.race_type === "sprint" && settings.keyGuidance !== "off";
  const isMissed = (i) => penalizedRef.current.has(i);

  // next token (used only for practice keyboard light-up)
  const nextToken = phase === "racing" ? tokens[pos] || null : null;
  const kbNext = guide ? nextToken : null;
  const gearChar = phase === "countdown" ? count
    : phase === "racing"
      ? (guide && nextToken ? (nextToken === "www." || nextToken === ".com" ? nextToken : nextToken.toUpperCase()) : "?")
      : "B";

  const done = charsDone(tokens, pos);
  const liveCps = elapsed > 0.2 ? +(done / (elapsed + mistakes * penalty)).toFixed(2) : 0;
  const livePenalty = (mistakes * penalty).toFixed(1);
  const tireLit = (pos_) => mistakes >= TIRE_ORDER.indexOf(pos_) + 1;

  const racers = useMemo(() => {
    const playerProg = target.length ? Math.min(1, done / target.length) : 0;
    const showGhosts = settings.ghostRacers !== "off";
    const list = showGhosts
      ? race.ghosts.map((g, i) => ({ name: g.name, tier: g.tier, progress: ghostProg[i] || 0, isPlayer: false }))
      : [];
    list.push({ name: "You", progress: playerProg, isPlayer: true, tier: "player", avatar: user?.profile_image_url });
    return list;
  }, [race.ghosts, ghostProg, done, target.length, settings.ghostRacers, user]);

  const prize = sponsor?.prizeCredits ? `$${(sponsor.prizeCredits / 100).toFixed(0)}` : null;

  // ---------------- RESULTS ----------------
  if (phase === "results" && result) {
    const isDqt = !!result.dqt;
    return (
      <div className="max-w-md mx-auto animate-slide-up" data-testid="race-results">
        <div className="flex items-center gap-3 mb-6">
          {isDqt ? <Flag className="text-race-red" size={26} /> : <Trophy className="text-race-volt" size={26} />}
          <h2 className="font-heading text-4xl tracking-tight" data-testid="result-headline">
            {isDqt ? "DQT - TIME DISQUALIFIED"
              : result.placement === 1 ? "P1 - CHECKERED FLAG" : `FINISHED P${result.placement}`}
          </h2>
        </div>
        {isDqt && (
          <div className="flex items-start gap-2 text-xs text-race-red border border-race-red/40 bg-race-red/10 p-3 mb-5" data-testid="result-dqt-note">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>You hit {MAX_MISTAKES}+ mistakes, so your <b>time is disqualified from prizes</b>. Your characters traversed are still credited to your record.</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 mb-5">
          {[
            { l: "CPS", v: result.cps, id: "result-cps", a: true },
            { l: "Accuracy", v: `${result.accuracy}%`, id: "result-accuracy" },
            { l: "Errors", v: result.errors, id: "result-errors" },
            { l: "Placement", v: isDqt ? "DQT" : `P${result.placement}/${result.field_size}`, id: "result-placement" },
          ].map((s) => (
            <div key={s.l} className="bg-race-surface border border-race-line p-4" data-testid={s.id}>
              <div className="overline">{s.l}</div>
              <div className={`tabular text-3xl mt-1 ${s.a ? "text-race-red" : "text-race-txt"}`}>{s.v}</div>
            </div>
          ))}
        </div>
        <div className="bg-race-surface border border-race-line p-4 mb-5 grid grid-cols-3 gap-2 tabular text-center">
          <div><div className="text-xl">{result.raw_time}s</div><div className="text-[10px] text-race-sub mt-1">RAW</div></div>
          <div><div className="text-xl text-race-red">+{result.penalty_time}s</div><div className="text-[10px] text-race-sub mt-1">PENALTY</div></div>
          <div><div className="text-xl text-race-volt">{result.final_time}s</div><div className="text-[10px] text-race-sub mt-1">FINAL</div></div>
        </div>
        {result.flagged && (
          <div className="flex items-center gap-2 text-xs text-race-red border border-race-red/40 bg-race-red/10 p-3 mb-5" data-testid="result-flag">
            <AlertTriangle size={14} /> Flagged for review ({result.flags.join(", ")}).
          </div>
        )}
        <div className="flex gap-3">
          {onAgain && (
            <button onClick={onAgain} data-testid="race-again-btn"
              className="flex items-center gap-2 bg-race-red hover:bg-race-redhover text-race-bg font-semibold px-6 py-3 rounded-sm transition-colors">
              <RotateCcw size={18} /> Race Again
            </button>
          )}
          <button onClick={onExit} data-testid="race-exit-btn"
            className="border border-race-line hover:border-race-txt text-race-txt px-6 py-3 rounded-sm transition-colors">Exit</button>
        </div>
      </div>
    );
  }

  // ---------------- VEHICLE (ready / countdown / racing) ----------------
  return (
    <div className="race-surface max-w-[430px] mx-auto pt-2">
      <div className="relative mx-3">
        {/* Four corner tires - outside the vehicle body so they never overlap the
            sponsor/ad space configured in the Contest Wizard. */}
        <Tire pos="tr" lit={tireLit("tr")} clickable={phase === "racing" && tireLit("tr") && !dqt} onClick={openRestart} showHint={phase === "racing" && !dqt} />
        <Tire pos="br" lit={tireLit("br")} clickable={phase === "racing" && tireLit("br") && !dqt} onClick={openRestart} />
        <Tire pos="bl" lit={tireLit("bl")} clickable={phase === "racing" && tireLit("bl") && !dqt} onClick={openRestart} />
        <Tire pos="tl" lit={tireLit("tl")} clickable={false} onClick={openRestart} />

        {/* Vehicle body */}
        <div className="relative bg-white text-neutral-900 rounded-2xl border border-neutral-300 shadow-[0_0_40px_rgba(0,0,0,0.6)] overflow-hidden">
        {flash > 0 && <div key={flash} className="pointer-events-none absolute inset-0 z-40 animate-error-pulse rounded-2xl" />}

        {/* Restart confirmation */}
        {showRestart && (
          <div className="absolute inset-0 z-50 bg-black/70 flex items-center justify-center rounded-2xl px-6" data-testid="restart-confirm">
            <div className="bg-white text-neutral-900 rounded-xl p-5 w-full max-w-[260px] text-center border border-neutral-300">
              <div className="font-heading text-3xl">RESTART?</div>
              <p className="text-xs text-neutral-600 mt-2 mb-4">Clears the track for a clean run. The race clock <b>keeps running</b> - you trade time for accuracy.</p>
              <div className="flex gap-2">
                <button data-testid="restart-cancel-btn" onClick={() => setShowRestart(false)}
                  className="flex-1 border border-neutral-300 hover:border-neutral-500 rounded-md py-2 text-sm font-medium">Cancel</button>
                <button data-testid="restart-confirm-btn" onClick={handleRestart}
                  className="flex-1 bg-race-red hover:bg-race-redhover text-white rounded-md py-2 text-sm font-semibold">Restart</button>
              </div>
            </div>
          </div>
        )}

        {/* Header: leave · gear · transmission */}
        <div className="flex items-center justify-between px-4 pt-4">
          <button onClick={onExit} data-testid="leave-race-btn" className="text-neutral-500 hover:text-neutral-900">
            <ChevronLeft size={26} />
          </button>
          <div className="flex flex-col items-center">
            <div className="min-w-[64px] h-14 px-3 bg-neutral-200 rounded-md flex items-center justify-center font-heading text-4xl text-neutral-900 leading-none"
              data-testid="gear-display">
              {gearChar}
            </div>
            <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-neutral-400 mt-1">{transmission}-Speed</span>
          </div>
          <div className="w-[26px]" />
        </div>

        {/* DQT banner */}
        {dqt && phase === "racing" && (
          <div className="mx-6 mt-3 flex items-center gap-2 bg-race-red text-white text-[11px] font-bold uppercase tracking-wide rounded-md px-3 py-1.5" data-testid="dqt-banner">
            <Flag size={12} /> Time Disqualified - finish for character credit
          </div>
        )}

        {/* Character Track Prompt - cursor HOLDS on a miss (turns red); www./.com are atomic. */}
        <div className="px-6 py-3 min-h-[104px] flex flex-col justify-center">
          {!guide && (
            <p className="font-body text-lg font-semibold mb-3 text-neutral-900" data-testid="trivia-question">{race.prompt}</p>
          )}
          <p className="font-mono text-xl md:text-2xl leading-relaxed break-words tracking-[0.08em]" data-testid="race-target">
            {tokens.map((tok, i) => {
              const isDone = i < pos;
              const isCur = i === pos && phase === "racing";
              const missed = isCur && isMissed(i);
              let display;
              if (isDone || guide) display = tok;
              else display = isCur ? "_" : "·";
              // Theme-proof inline colors so the character is ALWAYS high-contrast on the white card.
              let style = {};
              let cls = "";
              if (isDone) style = { color: "#16a34a", fontWeight: 700 };
              else if (missed) { style = { background: "#dc2626", color: "#fff", fontWeight: 800 }; cls = "rounded px-1"; }
              else if (isCur) { style = { background: "#111827", color: "#fff", fontWeight: 800 }; cls = "rounded px-1"; }
              else if (guide) style = { color: "#111827", fontWeight: 600 };
              else style = { color: "#cbd5e1" };
              return <span key={i} className={`${cls} ${tok === ".com" ? "whitespace-nowrap" : ""}`} style={style} data-testid={`track-char-${i}`}>{display}</span>;
            })}
          </p>
        </div>

        {/* Chronograph strip */}
        <div className="mx-6 mb-3 flex items-center justify-center tabular text-xs border-t border-neutral-200 pt-2 text-neutral-500">
          <span data-testid="race-clock" className="text-neutral-900 font-semibold">{elapsed.toFixed(2)}s</span>
        </div>

        {/* Sponsor / prize pill (featured on the vehicle) */}
        <div className="mx-6 mb-4">
          <div className="border border-neutral-300 rounded-full px-4 py-2 flex items-center justify-center gap-2" data-testid="sponsor-pill">
            {prize ? (
              <>
                <span className="font-heading text-2xl text-neutral-900 leading-none">{prize}</span>
                <span className="w-5 h-5 rounded bg-neutral-900 text-white text-xs font-bold flex items-center justify-center">$</span>
                <span className="font-semibold text-neutral-900 text-sm">{sponsor?.name || "Cash App"}</span>
              </>
            ) : (
              <span className="font-semibold text-neutral-500 text-sm uppercase tracking-wide">Free Practice · No Prize</span>
            )}
          </div>
        </div>

        {/* Keyboard tray (the transmission) - needle Speedometer sits directly above it. */}
        <div className="bg-neutral-100 border-t border-neutral-200 px-6 pt-2 pb-5 relative">
          <div className="flex items-end justify-between mb-1 px-1">
            <span className="font-mono text-[11px] font-semibold text-neutral-700 tabular pb-1" data-testid="race-cps-label">
              <Zap size={11} className="inline mb-0.5 mr-0.5" />{liveCps.toFixed(1)} CPS
            </span>
            <Speedometer cps={liveCps} accurateStreak={streak} />
            <span className="font-mono text-[11px] font-semibold text-neutral-500 tabular pb-1" data-testid="race-pen-label">+{livePenalty}S PEN</span>
          </div>
          {phase === "ready" && (
            <div className="absolute inset-0 z-30 bg-white/95 flex flex-col items-center justify-center gap-3">
              <p className="text-center text-sm text-neutral-500 max-w-[250px]">No delete key. The cursor holds on a miss - each missed character adds a {penalty}s penalty and lights a tire. Tap a red tire to restart clean, but the clock never stops. {MAX_MISTAKES} misses = DQT.</p>
              <button onClick={startCountdown} data-testid="start-race-btn"
                className="bg-neutral-900 hover:bg-black text-white font-heading text-3xl tracking-wide px-10 py-3 rounded-md transition-colors">
                START ENGINE
              </button>
            </div>
          )}
          {phase === "countdown" && (
            <div className="absolute inset-0 z-30 bg-white/95 flex items-center justify-center" data-testid="race-countdown">
              <div className="font-heading text-7xl text-neutral-900 animate-error-pulse">{count}</div>
            </div>
          )}
          <RacingKeyboard keys={race.keyboard} onPress={handlePress} nextToken={kbNext} disabled={phase !== "racing"} />
        </div>

        <div className="px-6 py-2 text-center text-[9px] font-mono uppercase tracking-[0.2em] text-neutral-400 border-t border-neutral-200">
          BR License · IBRA Sanctioned
        </div>
        </div>
      </div>

      {/* Real-time Racing Position Screen under the vehicle */}
      <div className="mx-3 mt-2">
        <RacePositions racers={racers} />
      </div>
    </div>
  );
}
